<template>
   <b-card variant="white" class="mx-auto standard-width">
      <b-form id="company-form" @submit.prevent>
         <h2 class="mb-3">General Information</h2>
         <!-- Company Name -->
         <b-form-group
            label="Company Name"
            label-for="input-company-name"
            invalid-feedback="Required"
         >
            <b-form-input
               id="input-company-name"
               ref="name"
               v-model="form.companyName"
               trim
               :state="!$v.form.companyName.$invalid"
            />
         </b-form-group>

         <b-form-row>
            <b-col md="6">
               <!-- FEIN -->
               <b-form-group
                  label="FEIN#"
                  label-for="input-fein"
                  invalid-feedback="Please enter a 9 digit number"
               >
                  <b-form-input
                     id="input-fein"
                     v-model="form.fein"
                     trim
                     :formatter="formatFein"
                     :state="form.fein ? !$v.form.fein.$invalid : null"
                  ></b-form-input>
               </b-form-group>
            </b-col>

            <b-col md="6">
               <!-- Incorporation Date -->
               <b-form-group label="Date of Incorporation" label-for="input-incorporation-date">
                  <v-date-picker v-model="form.incorporated" :input-debounce="500">
                     <template v-slot="{inputValue, inputEvents}">
                        <input
                           id="input-incorporation-date"
                           class="form-control"
                           :class="{
                              'is-valid': form.incorporated && !$v.form.incorporated.$invalid,
                              'is-invalid': form.incorporated && $v.form.incorporated.$invalid,
                           }"
                           :value="inputValue"
                           v-on="inputEvents"
                           placeholder="mm/dd/yyyy"
                        />
                     </template>
                  </v-date-picker>
               </b-form-group>
            </b-col>
         </b-form-row>

         <b-form-row>
            <b-col md="6">
               <!-- Entity Type -->
               <b-form-group label="Entity Type" label-for="input-entity-type">
                  <b-form-select
                     id="input-entity-type"
                     v-model="form.entityType"
                     :options="entityTypes"
                     :state="form.entityType ? !$v.form.entityType.$invalid : null"
                  ></b-form-select>
               </b-form-group>
            </b-col>

            <b-col md="6">
               <!-- Industry -->
               <b-form-group label="Industry" label-for="input-industry">
                  <b-form-select
                     id="input-industry"
                     v-model="form.industryId"
                     :options="industryOptions"
                     :state="form.industryId ? !$v.form.industryId.$invalid : null"
                  >
                     <template #first>
                        <b-form-select-option :value="null" disabled>
                           Please select one
                        </b-form-select-option>
                     </template>
                  </b-form-select>
               </b-form-group>
            </b-col>
         </b-form-row>

         <b-form-row>
            <b-col md="6">
               <!-- Filing Deadline -->
               <b-form-group label="Filing Deadline">
                  <b-form-row>
                     <b-col cols="6">
                        <b-form-select
                           id="input-filing-deadline-month"
                           :options="monthOptions"
                           v-model="form.filingDeadlineMonth"
                        >
                           <template #first>
                              <b-form-select-option :value="null" disabled>
                                 Month
                              </b-form-select-option>
                           </template>
                        </b-form-select>
                     </b-col>
                     <b-col cols="6">
                        <b-form-select
                           id="input-filing-deadline-day"
                           :options="filingDeadlineDayOptions"
                           :disabled="!form.filingDeadlineMonth"
                           v-model="form.filingDeadlineDay"
                        >
                           <template #first>
                              <b-form-select-option :value="null" disabled>
                                 Day
                              </b-form-select-option>
                           </template>
                        </b-form-select>
                     </b-col>
                  </b-form-row>
               </b-form-group>
            </b-col>

            <b-col md="6">
               <!-- Tax Year End -->
               <b-form-group label="Tax Year End">
                  <b-form-row>
                     <b-col cols="6">
                        <b-form-select
                           id="input-tax-year-end-month"
                           :options="monthOptions"
                           v-model="form.taxYearEndMonth"
                        >
                           <template #first>
                              <b-form-select-option :value="null" disabled>
                                 Month
                              </b-form-select-option>
                           </template>
                        </b-form-select>
                     </b-col>
                     <b-col cols="6">
                        <b-form-select
                           id="input-tax-year-end-day"
                           :options="taxYearEndDayOptions"
                           :disabled="!form.taxYearEndMonth"
                           v-model="form.taxYearEndDay"
                        >
                           <template #first>
                              <b-form-select-option :value="null" disabled>
                                 Day
                              </b-form-select-option>
                           </template>
                        </b-form-select>
                     </b-col>
                  </b-form-row>
               </b-form-group>
            </b-col>
         </b-form-row>

         <!-- Payroll Tax Election -->
         <b-form-checkbox
            id="cb-payroll-tax-election"
            class="mb-4"
            v-model="form.payrollTaxElection"
         >
            Payroll Tax Election
         </b-form-checkbox>

         <!-- State Credits -->
         <b-form-row class="mb-3">
            <b-col class="d-flex align-items-center">
               <h3 class="d-inline-block mb-0">State Credits</h3>
            </b-col>
            <b-col class="d-flex align-items-center justify-content-end">
               <div id="btn-add-state" class="add-btn" @click="addState">
                  <b-icon icon="plus-circle" v-b-tooltip.left="'Add a state'"></b-icon>
               </div>
            </b-col>
         </b-form-row>
         <StateForm
            v-for="(item, index) in form.stateCredits"
            :key="'sf' + index"
            :index="index"
            v-model="form.stateCredits[index]"
            @remove="removeState(index)"
            class="ml-3"
         />

         <h2 class="mb-3">History</h2>
         <!-- Year of 1st R&D Effort -->
         <b-form-group label="Year of First R&D Effort">
            <YearSelect
               tax-years
               or-earlier
               index="first-effort"
               v-model="form.firstRndEffort"
               :state="form.firstRndEffort ? !$v.form.firstRndEffort.$invalid : null"
            />
         </b-form-group>

         <!-- Startup Year -->
         <b-form-group label="Startup Year">
            <b-form-row>
               <b-col md="6">
                  <b-form-group
                     label="Start Date"
                     label-for="input-startup-year-start"
                     :invalid-feedback="startupYearFeedback"
                     :state="$v.form.startupYear.lower.$invalid ? false : null"
                  >
                     <v-date-picker
                        ref="startupYearStart"
                        timezone="UTC"
                        v-model="form.startupYear.lower"
                        :model-config="modelConfig"
                        :input-debounce="1000"
                        mode="date"
                        @input="onInputStartupYearStart"
                     >
                        <template v-slot="{inputValue, inputEvents}">
                           <input
                              id="input-startup-year-start"
                              class="form-control"
                              :class="{'is-invalid': $v.form.startupYear.lower.$invalid}"
                              :value="inputValue"
                              v-on="inputEvents"
                              placeholder="mm/dd/yyyy"
                           />
                        </template>
                     </v-date-picker>
                  </b-form-group>
               </b-col>
               <b-col md="6">
                  <b-form-group
                     label="End Date"
                     label-for="input-startup-year-end"
                     :state="$v.form.startupYear.upper.$invalid ? false : null"
                     invalid-feedback="required"
                  >
                     <v-date-picker
                        ref="startupYearEnd"
                        timezone="UTC"
                        v-model="form.startupYear.upper"
                        :model-config="modelConfig"
                        :input-debounce="1000"
                        mode="date"
                     >
                        <template v-slot="{inputValue, inputEvents}">
                           <input
                              id="input-startup-year-end"
                              class="form-control"
                              :class="{'is-invalid': $v.form.startupYear.upper.$invalid}"
                              :value="inputValue"
                              v-on="inputEvents"
                              placeholder="mm/dd/yyyy"
                           />
                        </template>
                     </v-date-picker>
                  </b-form-group>
               </b-col>
            </b-form-row>
         </b-form-group>

         <h2 class="mb-3">Company Contacts</h2>
         <!-- CPA Name -->
         <b-form-group label="CPA Name" label-for="input-cpa-name">
            <b-form-input
               id="input-cpa-name"
               v-model="form.cpaInfo.name"
               trim
               :state="form.cpaInfo.name ? !$v.form.cpaInfo.name.$invalid : null"
            ></b-form-input>
         </b-form-group>

         <!-- CPA Firm -->
         <b-form-group label="CPA Firm Name" label-for="input-cpa-firm">
            <b-form-input
               id="input-cpa-firm"
               v-model="form.cpaInfo.firm"
               trim
               :state="form.cpaInfo.firm ? !$v.form.cpaInfo.firm.$invalid : null"
            ></b-form-input>
         </b-form-group>

         <!-- CPA Email -->
         <b-form-group label="CPA Email" label-for="input-cpa-email">
            <b-form-input
               id="input-cpa-email"
               v-model="form.cpaInfo.email"
               trim
               :state="form.cpaInfo.email ? !$v.form.cpaInfo.email.$invalid : null"
            ></b-form-input>
         </b-form-group>

         <!-- Client Contact Name -->
         <b-form-group label="Client Contact Name" label-for="input-contact-name">
            <b-form-input
               id="input-contact-name"
               v-model="form.contactInfo.name"
               trim
               :state="form.contactInfo.name ? !$v.form.contactInfo.name.$invalid : null"
            ></b-form-input>
         </b-form-group>

         <!-- Client Contact Email -->
         <b-form-group label="Client Contact Email" label-for="input-contact-email">
            <b-form-input
               id="input-contact-email"
               v-model="form.contactInfo.email"
               trim
               :state="form.contactInfo.email ? !$v.form.contactInfo.email.$invalid : null"
            ></b-form-input>
         </b-form-group>

         <b-form-row class="mt-4">
            <b-col class="text-right">
               <b-button
                  id="btn-submit"
                  variant="primary"
                  :disabled="$v.form.$invalid"
                  @click="onSubmit"
               >
                  Submit
               </b-button>
            </b-col>
         </b-form-row>
      </b-form>
   </b-card>
</template>

<script>
import {required, email} from 'vuelidate/lib/validators';
import {mapGetters} from 'vuex';

import StateForm from '@/views/company/widgets/StateForm';
import YearSelect from '@/components/forms/YearSelect';

export default {
   name: 'CompanyForm',

   components: {
      StateForm,
      YearSelect,
   },

   props: {
      company: {
         type: Object,
      },

      isCreate: {
         type: Boolean,
         default: false,
      },
   },

   async mounted() {
      await this.blockingRequest('industries/loadIndustries');
      if (this.isCreate) {
         this.$refs.name.focus();
         this.form.industryId = this.defaultIndustryId;
      }
   },

   data() {
      return {
         form: this.company,
         modelConfig: {
            type: 'string',
            mask: 'YYYY-MM-DD',
         },
         entityTypes: [
            {value: null, text: 'Please select one'},
            {value: 'S_CORP', text: 'S Corp'},
            {value: 'C_CORP', text: 'C Corp'},
            {value: 'PARTNERSHIP', text: 'Partnership'},
         ],
         monthOptions: [
            {value: 1, text: 'January'},
            {value: 2, text: 'February'},
            {value: 3, text: 'March'},
            {value: 4, text: 'April'},
            {value: 5, text: 'May'},
            {value: 6, text: 'June'},
            {value: 7, text: 'July'},
            {value: 8, text: 'August'},
            {value: 9, text: 'September'},
            {value: 10, text: 'October'},
            {value: 11, text: 'November'},
            {value: 12, text: 'December'},
         ],
      };
   },

   computed: {
      ...mapGetters({
         industries: 'industries/industries',
         defaultIndustryId: 'industries/defaultIndustryId',
      }),

      industryOptions() {
         return this.industries.map((industry) => ({value: industry.id, text: industry.name}));
      },

      startupYearFeedback() {
         const v = this.$v.form.startupYear.lower;
         if (!v.startBeforeEnd) return 'End date must be after start date';
         if (!v.oneYearMax) return 'Startup year period cannot exceed one year';
         return null;
      },

      filingDeadlineDayOptions() {
         return this.getDayOptions(this.form.filingDeadlineMonth);
      },

      taxYearEndDayOptions() {
         return this.getDayOptions(this.form.taxYearEndMonth);
      },
   },

   watch: {
      'form.filingDeadlineMonth'() {
         this.form.filingDeadlineDay = null;
      },

      'form.taxYearEndMonth'() {
         this.form.taxYearEndDay = null;
      },
   },

   methods: {
      getDayOptions(month) {
         if (!month) return [];
         // Fixed at 29 for February since these are recurring annual dates, not tied to a specific year
         const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
         return [...Array(daysInMonth).keys()].map((day) => day + 1);
      },

      onSubmit() {
         this.$emit('submit', this.form);
      },

      formatFein(value) {
         value = value.replace(/\D/g, '');
         if (value.length >= 3 && '-' !== value[2]) {
            return value.substring(0, 2) + '-' + value.substring(2);
         }
         return value;
      },

      addState() {
         this.form.addState();
      },

      removeState(index) {
         this.form.deleteState(index);
      },

      onInputStartupYearStart(date) {
         if (date && !this.form.startupYear.upper) {
            const end = new Date(date);
            end.setFullYear(end.getFullYear() + 1);
            end.setDate(end.getDate() - 1);
            this.form.startupYear.upper = end.toISOString().split('T')[0];
         }
      },
   },

   validations: {
      form: {
         companyName: {
            required,
         },
         filingDeadlineMonth: {},
         filingDeadlineDay: {},
         fein: {
            format(value) {
               if (value) {
                  const re = /^\d{2}-?\d{7}$/;
                  return re.test(value);
               }
               return true;
            },
         },
         entityType: {},
         taxYearEndMonth: {},
         taxYearEndDay: {},
         industryId: {
            required,
         },
         stateCredits: {
            $each: {},
         },
         incorporated: {},
         firstRndEffort: {},
         startupYear: {
            lower: {
               startBeforeEnd() {
                  const {lower, upper} = this.form.startupYear;
                  if (!(lower && upper)) return true;
                  return lower < upper;
               },
               oneYearMax() {
                  const {lower, upper} = this.form.startupYear;
                  if (!(lower && upper)) return true;
                  const maxEnd = new Date(lower);
                  maxEnd.setFullYear(maxEnd.getFullYear() + 1);
                  return new Date(upper) <= maxEnd;
               },
            },
            upper: {},
         },
         cpaInfo: {
            name: {},
            firm: {},
            email: {
               email,
            },
         },
         contactInfo: {
            name: {},
            email: {
               email,
            },
         },
      },
   },
};
</script>
